<template>
  <div :class="{ 'shadow-form bg-white rounded-[15px]': !solid }">
    <div v-if="!solid">
      <div class="text-heading-5 text-primary-1">
        {{ t("payment.leadGuestDetails") }}
      </div>
      <div class="text-heading-6 text-[#8c8c8c] mt-1">
        {{ t("payment.formDescription") }}
      </div>
    </div>
    <div
      v-if="!isLoggedIn && loginFlag && loginSignupEnabled"
      class="bg-gray-3 mt-5 p-2 rounded-md"
      @click="promptLogin"
    >
      <div
        class="text-heading-6 text-primary-1 flex gap-1 text-center items-center justify-center cursor-pointer"
      >
        <IconPassengers class="text-[18px] !mb-0" />
        <div class="text-mobile-heading-5 md:text-heading-5">
          {{ t("login.title") }}
        </div>
        {{ t("login.or") }}
        <div class="text-mobile-heading-5 md:text-heading-5">
          {{ t("signup.title") }}
        </div>
        <span class="max-md:text-mobile-heading-6">
          {{ t("payment.fasterCheckout") }}
        </span>
      </div>
    </div>
    <form class="flex flex-col gap-3 mt-7">
      <div class="grid sm:grid-cols-2 grid-cols-1 gap-2 md:gap-4">
        <FormInput
          v-model:value="basicFields[0].value"
          :title="basicFields[0].label"
          :name="basicFields[0].name"
          :required="true"
          :check="check"
          :disabled="basicFields[0].disabled"
        />
        <FormInput
          v-model:value="basicFields[1].value"
          :title="basicFields[1].label"
          :name="basicFields[1].name"
          :required="true"
          :check="check"
          :disabled="basicFields[1].disabled"
        />
      </div>
      <FormInput
        v-model:value="basicFields[2].value"
        :title="basicFields[2].label"
        :name="basicFields[2].name"
        type="email"
        :required="true"
        :check="check"
        :disabled="basicFields[2].disabled"
      />
      <div class="grid sm:grid-cols-2 grid-cols-1 gap-2 md:gap-4">
        <div>
          <div class="text-primary-1 text-heading-6 mb-2 h-4 max-md:hidden">
            {{ t("payment.form.phoneNumber") }}
          </div>
          <div class="text-primary-1 text-heading-6 mb-2 h-4 md:hidden">
            {{ t("payment.form.countryCode") }}
          </div>
          <BaseAutocompleteOption
            v-model:option="option"
            class="w-full"
            :class="{
              'border border-red !rounded-[5px]': check && !option,
            }"
            main-class="!py-3 input-color !rounded-[5px] h-10"
            :options="options"
            :required="true"
            :placeholder="t('questions.chooseCountryCode')"
            :check="check"
          />
          <div
            v-if="check && !option"
            class="text-[11px] font-normal mt-1 text-red flex gap-1 items-center"
          >
            <icon-warning class="text-red text-[15px] !mb-0" />
            {{ t("requiredField") }}
          </div>
        </div>
        <div class="text-primary-1 text-heading-6 mb-[-16px] h-4 md:hidden">
          {{ t("payment.form.phoneNumber") }}
        </div>
        <FormInput
          v-model:value="basicFields[3].value"
          :required="true"
          :check="check"
          :disabled="basicFields[3].disabled"
        />
      </div>
    </form>
  </div>
</template>
<script setup lang="ts">
import FormInput from "./PaymentFormInput.vue";
import type { IOptions } from "../../types/general";
import { countryCodes } from "../../assets/js/countryCodes.js";
import { inject, onMounted, reactive, ref, watch } from "vue";
import { useRuntimeConfig } from "nuxt/app";
const { isLoggedIn, userProfile, profile } = useAuth();
const { t } = useI18n();
const props = defineProps({
  check: {
    type: Boolean,
    default: false,
  },
  solid: {
    type: Boolean,
    default: false,
  },
  email: {
    type: String,
    default: "",
  },
  emailDisabled: {
    type: Boolean,
    default: false,
  },
  loginFlag: {
    type: Boolean,
    default: true,
  },
});

const config = useRuntimeConfig() as unknown as RuntimeConfiguration;
const loginSignupEnabled = config.public.features.loginSignup;
const option = ref<IOptions>();
const options = reactive([]) as IOptions[];
countryCodes.map((x) => {
  options.push({
    id: x.dial_code,
    title: x.name,
  });
});
const basicFields = reactive([
  {
    id: "firstname",
    label: t("payment.form.firstName"),
    value: "",
    name: "first-name",
    disabled: false,
  },
  {
    id: "lastname",
    label: t("payment.form.lastName"),
    value: "",
    name: "last-name",
    disabled: false,
  },
  {
    id: "email",
    label: t("payment.form.email"),
    value: "",
    name: "user-email",
    disabled: false,
  },
  {
    id: "phoneNumber",
    label: t("payment.form.phoneNumber"),
    value: "",
    name: "user-phone",
    disabled: false,
  },
  {
    id: "phoneCode",
    label: "Phone Number",
    value: "",
    disabled: false,
  },
]);
const emit = defineEmits(["basic-changed"]);
watch(
  basicFields,
  (newVal) => {
    emit("basic-changed", newVal);
  },
  { deep: true }
);
watch(
  () => props.email,
  (newVal) => {
    basicFields[2].value = props.email;
  },
  { immediate: true }
);
watch(
  () => props.emailDisabled,
  (newVal) => {
    basicFields[2].disabled = props.emailDisabled;
  },
  { immediate: true }
);
watch(
  option,
  (newVal) => {
    //   basicFields.value[3],
    //   (newVal) => {
    basicFields[4].value = `${option.value?.id}`;
  },
  { deep: true }
);
onMounted(async () => {
  if (isLoggedIn.value) {
    await profile();
    const profileTmp = userProfile.value;
    if (profileTmp) {
      basicFields[0].value = profileTmp.first_name || "";
      basicFields[1].value = profileTmp.last_name || "";
      basicFields[2].value = profileTmp.email || "";
      basicFields[2].disabled = true;
      basicFields[3].value = profileTmp.phone_number || "";
      option.value = options.find(
        (opt) => opt.id == profileTmp.phone_country_code
      );
    }
  }
});
const openLogin = inject<() => void>("openLogin");
function promptLogin() {
  if (openLogin) {
    openLogin();
  }
}
</script>
